import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import type { EntitlementResponse } from '@api/types/entitlements';
import SkillInfoDialog from '@components/Marketplace/SkillInfoDialog';
import { FeatureGate, useFeatureGate } from '@featureFlags/index';
import { APP_TABS, type AppTab } from '../../pages/Apps/appTabs';
import AppsThemeBoundary from '../../pages/Apps/AppsThemeBoundary';
import CreateAppDialog from '../../pages/Apps/builder/create/CreateAppDialog';
import {
  ALL_TAB,
  FOOTER_VIEW_APPS,
  FOOTER_VIEW_MARKETPLACE,
  type FooterView,
} from './AppsFooter.constants';
import FooterAppsContent from './FooterAppsContent';
import FooterHeader from './FooterHeader';
import FooterSkillsContent from './FooterSkillsContent';
import { useAppsFooter } from './useAppsFooter';
import { useFooterApps } from './useFooterApps';
import { skillsInTab, useFooterSkills } from './useFooterSkills';
import { Body, Root } from './AppsFooter.styled';

const isAppTab = (tab: string): tab is AppTab => (APP_TABS as readonly string[]).includes(tab);

const AppsFooter: React.FC = () => {
  const intl = useIntl();
  const appsGate = useFeatureGate(FeatureGate.OS_APPS);
  const htmlViewGate = useFeatureGate(FeatureGate.OS_APP_HTML_VIEW);
  const builderEnabled = useFeatureGate(FeatureGate.OS_APP_BUILDER);
  const appsEnabled = appsGate && htmlViewGate;
  const { open, expanded, view: chosenView, setOpen, setExpanded, setView } = useAppsFooter();

  const [tabsOpen, setTabsOpen] = useState(false);
  const [appTab, setAppTab] = useState<AppTab>(ALL_TAB);
  const [skillTab, setSkillTab] = useState(ALL_TAB);
  const [creating, setCreating] = useState(false);
  const [skillInfo, setSkillInfo] = useState<EntitlementResponse | null>(null);

  const view = appsEnabled ? chosenView : FOOTER_VIEW_MARKETPLACE;
  const tabsShown = expanded || tabsOpen;
  const bodyShown = expanded || open;
  const visible = bodyShown || tabsShown;
  const apps = useFooterApps(appsEnabled && view === FOOTER_VIEW_APPS && visible, builderEnabled);
  const skills = useFooterSkills(view === FOOTER_VIEW_MARKETPLACE && visible);
  const activeSkillTab = skills.tabs.some((tab) => tab.id === skillTab) ? skillTab : ALL_TAB;

  const bodyId = useId();
  const expandRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  // Set only by × and Escape: leaving the page also closes the gallery, and focus belongs to the new page then.
  const refocusExpand = useRef(false);
  const dialogOpen = creating || skillInfo !== null;

  const closeGallery = useCallback(() => {
    refocusExpand.current = true;
    setExpanded(false);
  }, [setExpanded]);

  useEffect(() => {
    if (expanded) {
      closeRef.current?.focus();
      return;
    }
    if (refocusExpand.current) expandRef.current?.focus();
    refocusExpand.current = false;
  }, [expanded]);

  useEffect(() => {
    if (!expanded || dialogOpen) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeGallery();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [expanded, dialogOpen, closeGallery]);

  const selectTitle = (target: FooterView) => {
    if (target === view) {
      setTabsOpen((shown) => !shown);
      return;
    }
    setView(target);
    setOpen(true);
  };

  const selectTab = (tab: string) => {
    if (view === FOOTER_VIEW_APPS && isAppTab(tab)) setAppTab(tab);
    if (view === FOOTER_VIEW_MARKETPLACE) setSkillTab(tab);
    setOpen(true);
  };

  return (
    <AppsThemeBoundary>
      <Root
        $open={bodyShown}
        $expanded={expanded}
        aria-label={
          expanded
            ? intl.formatMessage({ id: 'appsFooter.gallery', defaultMessage: 'All apps' })
            : intl.formatMessage({ id: 'appsFooter.label', defaultMessage: 'Apps and marketplace' })
        }
        data-testid="apps-footer"
        data-state={expanded ? 'gallery' : (open && 'open') || 'collapsed'}
      >
        <FooterHeader
          appsEnabled={appsEnabled}
          view={view}
          tabsShown={tabsShown}
          tabs={view === FOOTER_VIEW_APPS ? apps.tabs : skills.tabs}
          activeTab={view === FOOTER_VIEW_APPS ? appTab : activeSkillTab}
          open={open}
          expanded={expanded}
          bodyId={bodyId}
          expandRef={expandRef}
          closeRef={closeRef}
          onTitle={selectTitle}
          onTab={selectTab}
          onToggleOpen={() => setOpen(!open)}
          onExpand={() => setExpanded(true)}
          onClose={closeGallery}
        />
        <Body id={bodyId} $expanded={expanded} hidden={!bodyShown}>
          {bodyShown && view === FOOTER_VIEW_APPS && (
            <FooterAppsContent
              data={apps}
              tab={appTab}
              grid={expanded}
              canCreate={builderEnabled}
              onCreate={() => setCreating(true)}
            />
          )}
          {bodyShown && view === FOOTER_VIEW_MARKETPLACE && (
            <FooterSkillsContent
              skills={skillsInTab(skills.skills, activeSkillTab)}
              grid={expanded}
              isLoading={skills.isLoading}
              isError={skills.isError}
              onDownload={setSkillInfo}
            />
          )}
        </Body>
      </Root>
      <CreateAppDialog open={creating} onClose={() => setCreating(false)} />
      <SkillInfoDialog
        show={skillInfo !== null}
        onClose={() => setSkillInfo(null)}
        title={skillInfo?.displayName ?? ''}
        content={skillInfo?.infoText ?? ''}
      />
    </AppsThemeBoundary>
  );
};

export default AppsFooter;
